import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import {
  organizationService,
  OrgMember,
  OrgInvitation,
  OrgRole,
} from '../services/organizationService';
import {
  OrgPermissions,
  PERMISSION_LABELS,
  DEFAULT_PERMISSIONS,
} from '../types/permissions';
import '../styles/Users.css';
import '../styles/Pages.css';
import { UserListSkeleton } from '../components/SkeletonLoader';

type Tab = 'members' | 'roles';

// ─── Permission toggle grid ───────────────────────────────────

// Full list shown when viewing/editing existing role permissions
const PERM_ORDER_FULL: (keyof OrgPermissions)[] = [
  'projects', 'onboarding', 'customers',
  'product_insights', 'product_prioritize', 'product_features', 'product_initiatives', 'product_releases', 'product_objectives',
  'taskboards', 'whiteboards', 'documents', 'meetings', 'messenger', 'settings',
  'users', 'users_manage', 'roles_manage',
];

// Simplified list for new custom role form — merges the three team permissions into one
const PERM_ORDER_CUSTOM: (keyof OrgPermissions)[] = [
  'projects', 'onboarding', 'customers',
  'product_insights', 'product_prioritize', 'product_features', 'product_initiatives', 'product_releases', 'product_objectives',
  'taskboards', 'whiteboards', 'documents', 'meetings', 'messenger', 'settings', 'users',
];

const CUSTOM_PERM_LABELS: Partial<Record<keyof OrgPermissions, string>> = {
  users: 'Team Management (view members, invite & manage, manage roles)',
};

function enforceImplications(
  perms: OrgPermissions,
  changed: keyof OrgPermissions,
  value: boolean,
  merged = false
): OrgPermissions {
  const next = { ...perms, [changed]: value };
  if (changed === 'users') {
    if (merged) {
      next.users_manage = value;
      next.roles_manage = value;
    } else if (!value) {
      next.users_manage = false;
      next.roles_manage = false;
    }
  }
  if (!merged && (changed === 'users_manage' || changed === 'roles_manage') && value) {
    next.users = true;
  }
  return next;
}

interface PermissionEditorProps {
  permissions: OrgPermissions;
  readonly?: boolean;
  merged?: boolean;
  onChange: (next: OrgPermissions) => void;
}

const PermissionEditor: React.FC<PermissionEditorProps> = ({ permissions, readonly, merged, onChange }) => {
  const order = merged ? PERM_ORDER_CUSTOM : PERM_ORDER_FULL;
  return (
    <div className="perm-grid">
      {order.map((key) => (
        <label key={key} className={`perm-row ${readonly ? 'perm-readonly' : ''}`}>
          <span className="perm-label">
            {(merged && CUSTOM_PERM_LABELS[key]) || PERMISSION_LABELS[key]}
          </span>
          <input
            type="checkbox"
            className="perm-toggle"
            checked={permissions[key]}
            disabled={readonly}
            onChange={(e) => onChange(enforceImplications(permissions, key, e.target.checked, merged))}
          />
          <span className="perm-toggle-track" />
        </label>
      ))}
    </div>
  );
};

// ─── Add User Modal ───────────────────────────────────────────
interface AddUserModalProps {
  roles: OrgRole[];
  onClose: () => void;
  onSubmit: (data: {
    name: string; email: string; designation: string;
    phone: string; address: string; roleId: string;
  }) => Promise<void>;
}

const AddUserModal: React.FC<AddUserModalProps> = ({ roles, onClose, onSubmit }) => {
  const assignableRoles = roles.filter(r => r.level !== 'owner');
  const defaultRole = assignableRoles.find(r => r.name === 'Employee') ?? assignableRoles[0];

  const [name, setName]               = useState('');
  const [email, setEmail]             = useState('');
  const [designation, setDesignation] = useState('');
  const [phone, setPhone]             = useState('');
  const [address, setAddress]         = useState('');
  const [roleId, setRoleId]           = useState(defaultRole?.id ?? '');
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !roleId) return;
    setError('');
    setSubmitting(true);
    try {
      await onSubmit({ name: name.trim(), email: email.trim(), designation, phone, address, roleId });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to send invitation');
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Add User</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && <div className="users-error">{error}</div>}

          <div className="modal-row">
            <label className="modal-label">Full Name <span className="modal-required">*</span></label>
            <input
              type="text"
              className="users-input"
              placeholder="e.g. John Smith"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={submitting}
            />
          </div>

          <div className="modal-row">
            <label className="modal-label">Email <span className="modal-required">*</span></label>
            <input
              type="email"
              className="users-input"
              placeholder="colleague@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={submitting}
            />
          </div>

          <div className="modal-row">
            <label className="modal-label">Designation</label>
            <input
              type="text"
              className="users-input"
              placeholder="e.g. Product Manager"
              value={designation}
              onChange={(e) => setDesignation(e.target.value)}
              disabled={submitting}
            />
          </div>

          <div className="modal-row">
            <label className="modal-label">Phone</label>
            <input
              type="tel"
              className="users-input"
              placeholder="+1 555 000 0000"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={submitting}
            />
          </div>

          <div className="modal-row">
            <label className="modal-label">Address</label>
            <input
              type="text"
              className="users-input"
              placeholder="City, Country"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              disabled={submitting}
            />
          </div>

          <div className="modal-row">
            <label className="modal-label">Role <span className="modal-required">*</span></label>
            <select
              className="users-input"
              value={roleId}
              onChange={(e) => setRoleId(e.target.value)}
              disabled={submitting}
              required
            >
              {assignableRoles.length === 0 && (
                <option value="" disabled>No roles available</option>
              )}
              {assignableRoles.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>

          <div className="modal-actions">
            <button type="button" className="modal-cancel-btn" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="modal-submit-btn" disabled={submitting}>
              {submitting ? 'Sending Invite...' : 'Send Invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────
const Users: React.FC = () => {
  const { currentOrg, user } = useAuth();
  const { can, isAdmin } = usePermissions();

  const [tab, setTab] = useState<Tab>('members');

  const [members, setMembers]         = useState<OrgMember[]>([]);
  const [invitations, setInvitations] = useState<OrgInvitation[]>([]);
  const [roles, setRoles]             = useState<OrgRole[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(true);

  const [showAddModal, setShowAddModal] = useState(false);
  const [copiedLink, setCopiedLink]     = useState('');

  // Roles tab state
  const [expandedRoleId, setExpandedRoleId] = useState<string | null>(null);
  const [editingPerms, setEditingPerms]     = useState<Record<string, OrgPermissions>>({});
  const [savingRoleId, setSavingRoleId]     = useState<string | null>(null);

  const [showCreateRole, setShowCreateRole] = useState(false);
  const [newRoleName, setNewRoleName]       = useState('');
  const [newRoleDesc, setNewRoleDesc]       = useState('');
  const [newRoleLevel, setNewRoleLevel]     = useState<'admin' | 'member'>('member');
  const [newRolePerms, setNewRolePerms]     = useState<OrgPermissions>({ ...DEFAULT_PERMISSIONS });
  const [creatingRole, setCreatingRole]     = useState(false);
  const [createRoleError, setCreateRoleError] = useState('');

  const [toast, setToast] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const loadData = useCallback(async () => {
    if (!currentOrg) return;
    setLoadingMembers(true);
    try {
      const [m, inv, r] = await Promise.all([
        organizationService.getMembersWithProfiles(currentOrg.id),
        organizationService.getPendingInvitations(currentOrg.id),
        organizationService.getRoles(currentOrg.id),
      ]);
      const memberEmails = new Set(m.map(mem => mem.email?.toLowerCase()).filter(Boolean));
      setMembers(m);
      setInvitations(inv.filter(i => !memberEmails.has(i.email?.toLowerCase())));
      setRoles(r);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingMembers(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]); // primitive string — stable reference, prevents infinite re-render

  useEffect(() => { loadData(); }, [loadData]);

  const handleAddUser = async (data: {
    name: string; email: string; designation: string;
    phone: string; address: string; roleId: string;
  }) => {
    if (!currentOrg) return;
    const selectedRole = roles.find(r => r.id === data.roleId);
    await organizationService.createInvitation(
      currentOrg.id, data.email, data.roleId, selectedRole?.name ?? '',
      currentOrg.name,
      { name: data.name, designation: data.designation, phone: data.phone, address: data.address }
    );
    await loadData();
    showToast(`Invite sent to ${data.email}`);
  };

  const handleCopyLink = (token: string) => {
    const link = `${window.location.origin}/invite/${token}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(token);
    setTimeout(() => setCopiedLink(''), 2000);
    showToast('Invite link copied!');
  };

  const handleRevoke = async (invId: string) => {
    try {
      await organizationService.revokeInvitation(invId);
      await loadData();
      showToast('Invitation revoked');
    } catch { showToast('Failed to revoke'); }
  };

  const handleRoleChange = async (memberId: string, userId: string, newRoleId: string) => {
    if (userId === user?.id) { showToast("You can't change your own role"); return; }
    const role = roles.find(r => r.id === newRoleId);
    if (!role || !currentOrg) return;
    const member = members.find(m => m.user_id === userId);
    if (member?.role === 'owner') { showToast("The Owner role cannot be changed"); return; }
    try {
      await organizationService.updateMemberRole(currentOrg.id, userId, newRoleId, role.level as 'admin' | 'member');
      await loadData();
      showToast('Role updated');
    } catch { showToast('Failed to update role'); }
  };

  const handleRemoveMember = async (userId: string) => {
    if (userId === user?.id) { showToast("You can't remove yourself"); return; }
    const member = members.find(m => m.user_id === userId);
    if (member?.role === 'owner') { showToast("Cannot remove the Owner"); return; }
    if (!currentOrg || !window.confirm('Remove this member?')) return;
    try {
      await organizationService.removeMember(currentOrg.id, userId);
      await loadData();
      showToast('Member removed');
    } catch { showToast('Failed to remove member'); }
  };

  const handleToggleActive = async (userId: string, currentlyActive: boolean) => {
    if (userId === user?.id) { showToast("You can't disable yourself"); return; }
    const member = members.find(m => m.user_id === userId);
    if (member?.role === 'owner') { showToast("Cannot disable the Owner"); return; }
    if (!currentOrg) return;
    const action = currentlyActive ? 'disable' : 'enable';
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} this member?`)) return;
    try {
      await organizationService.toggleMemberActive(currentOrg.id, userId, !currentlyActive);
      await loadData();
      showToast(`Member ${action}d`);
    } catch { showToast(`Failed to ${action} member`); }
  };

  // Roles handlers
  const handleExpandRole = (roleId: string) => {
    if (expandedRoleId === roleId) { setExpandedRoleId(null); return; }
    setExpandedRoleId(roleId);
    const role = roles.find(r => r.id === roleId);
    if (role) setEditingPerms(prev => ({ ...prev, [roleId]: { ...role.permissions } }));
  };

  const handleSavePermissions = async (role: OrgRole) => {
    setSavingRoleId(role.id);
    try {
      await organizationService.updateRole(role.id, { permissions: editingPerms[role.id] });
      await loadData();
      setExpandedRoleId(null);
      showToast(`"${role.name}" permissions saved`);
    } catch { showToast('Failed to save permissions'); }
    finally { setSavingRoleId(null); }
  };

  const handleCreateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateRoleError('');
    if (!newRoleName.trim() || !currentOrg) return;
    setCreatingRole(true);
    try {
      await organizationService.createRole(
        currentOrg.id, newRoleName.trim(), newRoleDesc.trim(), 'member', newRolePerms
      );
      await loadData();
      setShowCreateRole(false);
      setNewRoleName('');
      setNewRoleDesc('');
      setNewRoleLevel('member');
      setNewRolePerms({ ...DEFAULT_PERMISSIONS });
      showToast(`Role "${newRoleName}" created`);
    } catch (err: any) {
      setCreateRoleError(err.message || 'Failed to create role');
    } finally {
      setCreatingRole(false);
    }
  };

  const handleDeleteRole = async (role: OrgRole) => {
    if (role.member_count && role.member_count > 0) {
      showToast(`Cannot delete "${role.name}" — ${role.member_count} member(s) assigned`);
      return;
    }
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    try {
      await organizationService.deleteRole(role.id);
      await loadData();
      showToast(`Role "${role.name}" deleted`);
    } catch { showToast('Failed to delete role'); }
  };

  const isExpiredInv = (iso: string) => new Date(iso) < new Date();
  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const assignableRoles = roles.filter(r => r.level !== 'owner');

  return (
    <Layout>
      <div className="users-page">
        {toast && <div className="users-toast">{toast}</div>}

        {showAddModal && (
          <AddUserModal
            roles={roles}
            onClose={() => setShowAddModal(false)}
            onSubmit={handleAddUser}
          />
        )}

        {/* ── Header ─────────────────────────────────────────── */}
        <div className="users-header">
          <div>
            <div className="page-breadcrumb">
              <div className="page-breadcrumb-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <h1 className="page-breadcrumb-title">Users</h1>
            </div>
            <p className="users-subtitle">
              {currentOrg?.name} &mdash; {members.length} member{members.length !== 1 ? 's' : ''}
              {invitations.length > 0 && `, ${invitations.length} pending`}
            </p>
          </div>
          {can('users_manage') && (
            <button className="users-add-btn btn-primary-cta" onClick={() => setShowAddModal(true)}>
              + Add User
            </button>
          )}
        </div>

        {/* ── Tabs ───────────────────────────────────────────── */}
        <div className="users-tabs">
          <button
            className={`users-tab ${tab === 'members' ? 'active' : ''}`}
            onClick={() => setTab('members')}
          >
            Members
          </button>
          {can('roles_manage') && (
            <button
              className={`users-tab ${tab === 'roles' ? 'active' : ''}`}
              onClick={() => setTab('roles')}
            >
              Roles
            </button>
          )}
        </div>

        {/* ── MEMBERS TAB ─────────────────────────────────────── */}
        {tab === 'members' && (
          <div className="users-list-section">
            {loadingMembers ? (
              <UserListSkeleton count={5} />
            ) : (
              <>
                {/* Member rows */}
                <div className="users-list-table">
                  <div className="users-list-header">
                    <span>User</span>
                    <span>Designation</span>
                    <span>Role</span>
                    <span>Status</span>
                    {isAdmin && <span>Actions</span>}
                  </div>

                  {members.map((m) => {
                    const isMe = m.user_id === user?.id;
                    const isThisOwner = m.role === 'owner';
                    const canEdit = isAdmin && !isMe && !isThisOwner;

                    return (
                      <div key={m.id} className={`users-list-row ${!m.is_active ? 'member-disabled' : ''}`}>
                        {/* User info */}
                        <div className="users-list-user">
                          <div className="users-member-avatar">
                            {(m.full_name || m.email || '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="users-member-info">
                            <span className="users-member-name">
                              {m.full_name || '—'}
                              {isMe && <span className="users-you-badge">You</span>}
                            </span>
                            <span className="users-member-email">{m.email || '—'}</span>
                            <span className="users-member-meta">Joined {formatDate(m.joined_at)}</span>
                          </div>
                        </div>

                        {/* Designation */}
                        <div className="users-list-cell users-designation">
                          {(m as any).designation || '—'}
                        </div>

                        {/* Role */}
                        <div className="users-list-cell">
                          {canEdit ? (
                            <select
                              className="users-role-select"
                              value={m.role_id}
                              onChange={(e) => handleRoleChange(m.id, m.user_id, e.target.value)}
                              disabled={!m.is_active}
                            >
                              {assignableRoles.map(r => (
                                <option key={r.id} value={r.id}>{r.name}</option>
                              ))}
                            </select>
                          ) : (
                            <span className={`users-role-badge users-role-${m.role}`}>
                              {m.role_name || m.role}
                            </span>
                          )}
                        </div>

                        {/* Status */}
                        <div className="users-list-cell">
                          <span className={`users-status-badge ${m.is_active ? 'status-active' : 'status-disabled'}`}>
                            {m.is_active ? 'Active' : 'Disabled'}
                          </span>
                        </div>

                        {/* Actions */}
                        {isAdmin && (
                          <div className="users-list-cell users-list-actions">
                            {canEdit && (
                              <>
                                <button
                                  className={`users-toggle-btn ${m.is_active ? 'disable' : 'enable'}`}
                                  onClick={() => handleToggleActive(m.user_id, m.is_active)}
                                >
                                  {m.is_active ? 'Disable' : 'Enable'}
                                </button>
                                <button
                                  className="users-remove-btn"
                                  onClick={() => handleRemoveMember(m.user_id)}
                                  title="Remove member"
                                >✕</button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Pending invitations in the same list */}
                  {invitations.map((inv) => {
                    const expired = isExpiredInv(inv.expires_at);
                    return (
                      <div key={inv.id} className={`users-list-row pending-row ${expired ? 'expired-row' : ''}`}>
                        <div className="users-list-user">
                          <div className="users-member-avatar pending-avatar">
                            {inv.email.charAt(0).toUpperCase()}
                          </div>
                          <div className="users-member-info">
                            <span className="users-member-name">
                              {(inv as any).metadata?.name || inv.email}
                            </span>
                            <span className="users-member-email">{inv.email}</span>
                            <span className="users-member-meta">
                              {expired ? 'Expired' : `Expires ${formatDate(inv.expires_at)}`}
                            </span>
                          </div>
                        </div>
                        <div className="users-list-cell users-designation">
                          {(inv as any).metadata?.designation || '—'}
                        </div>
                        <div className="users-list-cell">
                          <span className="users-role-badge users-role-member">
                            {inv.role_name || inv.role}
                          </span>
                        </div>
                        <div className="users-list-cell">
                          <span className="users-status-badge status-pending">Pending</span>
                        </div>
                        {isAdmin && (
                          <div className="users-list-cell users-list-actions">
                            {!expired && (
                              <button
                                className={`users-copy-btn-sm ${copiedLink === inv.token ? 'copied' : ''}`}
                                onClick={() => handleCopyLink(inv.token)}
                              >
                                {copiedLink === inv.token ? 'Copied!' : 'Copy Link'}
                              </button>
                            )}
                            <button className="users-revoke-btn" onClick={() => handleRevoke(inv.id)}>
                              Revoke
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {members.length === 0 && invitations.length === 0 && (
                  <div className="users-empty">No members yet.</div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── ROLES TAB ───────────────────────────────────────── */}
        {tab === 'roles' && (
          <div className="roles-section">
            {roles.map((role) => {
              const isExpanded   = expandedRoleId === role.id;
              // Only non-system custom roles can be edited
              const canEdit      = isAdmin && !role.is_system;
              const currentPerms = editingPerms[role.id] ?? role.permissions;

              return (
                <div key={role.id} className={`role-card ${isExpanded ? 'expanded' : ''}`}>
                  <div className="role-card-header" onClick={() => canEdit && handleExpandRole(role.id)}>
                    <div className="role-card-meta">
                      <div className="role-card-name-row">
                        <span className="role-card-name">{role.name}</span>
                        {role.is_system && (
                          <span className="role-system-badge">Default</span>
                        )}
                        <span className={`role-level-badge role-level-${role.level}`}>
                          {role.level}
                        </span>
                      </div>
                      <span className="role-card-desc">
                        {role.description || 'No description'}
                        {role.member_count !== undefined && (
                          <span className="role-member-count">
                            &nbsp;&bull;&nbsp;{role.member_count} member{role.member_count !== 1 ? 's' : ''}
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="role-card-actions" onClick={(e) => e.stopPropagation()}>
                      {canEdit && (
                        <button className="role-edit-btn" onClick={() => handleExpandRole(role.id)}>
                          {isExpanded ? 'Close' : 'Edit Permissions'}
                        </button>
                      )}
                      {!role.is_system && isAdmin && (
                        <button className="role-delete-btn" onClick={() => handleDeleteRole(role)}>
                          Delete
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Show permissions preview for system roles, edit for custom */}
                  {isExpanded && (
                    <div className="role-card-body">
                      {role.is_system ? (
                        <>
                          <p className="role-immutable-note">
                            Default roles cannot be modified. Create a custom role to adjust permissions.
                          </p>
                          <PermissionEditor permissions={currentPerms} readonly onChange={() => {}} />
                        </>
                      ) : (
                        <>
                          <PermissionEditor
                            permissions={currentPerms}
                            onChange={(next) =>
                              setEditingPerms(prev => ({ ...prev, [role.id]: next }))
                            }
                          />
                          <div className="role-save-row">
                            <button
                              className="role-save-btn"
                              onClick={() => handleSavePermissions(role)}
                              disabled={savingRoleId === role.id}
                            >
                              {savingRoleId === role.id ? 'Saving...' : 'Save Permissions'}
                            </button>
                            <button className="role-cancel-btn" onClick={() => setExpandedRoleId(null)}>
                              Cancel
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {isAdmin && (
              <div className="role-create-section">
                {!showCreateRole ? (
                  <button className="role-create-trigger" onClick={() => setShowCreateRole(true)}>
                    + Create Custom Role
                  </button>
                ) : (
                  <div className="role-card expanded">
                    <div className="role-card-header">
                      <span className="role-card-name">New Custom Role</span>
                    </div>
                    <div className="role-card-body">
                      <form onSubmit={handleCreateRole} className="role-create-form">
                        {createRoleError && <div className="users-error">{createRoleError}</div>}
                        <div className="role-create-fields">
                          <input
                            className="users-input"
                            placeholder="Role name (e.g. Designer, QA, Lead)"
                            value={newRoleName}
                            onChange={(e) => setNewRoleName(e.target.value)}
                            required
                            disabled={creatingRole}
                          />
                          <input
                            className="users-input"
                            placeholder="Description (optional)"
                            value={newRoleDesc}
                            onChange={(e) => setNewRoleDesc(e.target.value)}
                            disabled={creatingRole}
                          />
                        </div>
                        <p className="role-perms-heading">Feature Permissions</p>
                        <PermissionEditor permissions={newRolePerms} merged onChange={setNewRolePerms} />
                        <div className="role-save-row">
                          <button type="submit" className="role-save-btn" disabled={creatingRole}>
                            {creatingRole ? 'Creating...' : 'Create Role'}
                          </button>
                          <button
                            type="button"
                            className="role-cancel-btn"
                            onClick={() => { setShowCreateRole(false); setCreateRoleError(''); }}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Users;
